import { displayErrorsObject } from './form-errors';
import Pristine from 'pristinejs';

import { PickerDisplayMode } from 'filestack-js';
import { FILESTACK_PUBLIC_API_KEY, MB_SIZE } from '../../../app/Constants/Filestack';
import FilestackPickerUI from './filestack-picker-ui';
import { deleteMediaFromBackend } from './utils';

/*
* Add the class 'js-form' to the form to get it to submit and be validated using Pristine
* Add a 'data-route' attribute to the form to specify where it will submit to (use a route helper in the Edge template)
* Add a div.success-wrapper within the form for the success message
* To specify the success message, add the 'data-msg' attribute to the div.success-wrapper
* (otherwise the message will default to ' Success!')
* Add a div.error-wrapper within the form for any error messages
*/

// Enable or disable a form's inputs
function updateInputs(form: HTMLFormElement, disable: boolean) {
  const inputs = form.querySelectorAll('input, textarea, select, button');
  inputs.forEach((input) => {
    const recaptchaSubmitBtnIds: any = ['form-submit-2', 'form-submit-3'];

    if (disable) {
      input.setAttribute('disabled', 'true');
    } else {
      if (form.getAttribute('data-form') === 'contest-entry'
        && recaptchaSubmitBtnIds.includes(input.getAttribute('id'))
      ) {
        return;
      }
      input.removeAttribute('disabled');
    }
  });
}

function showSuccess(form: HTMLFormElement) {
  const successWrapper = form.querySelector('.success-wrapper') as HTMLElement | null;
  if (!successWrapper) {
    return;
  }
  const successMsg = successWrapper.getAttribute('data-msg')
    ? successWrapper.getAttribute('data-msg')
    : 'Success!';

  const successMsgDesc: any = successWrapper.getAttribute('data-msg-desc')
    ? successWrapper.getAttribute('data-msg-desc')
    : null;

  const msgWrapper = document.createElement('span');
  // You can include escaped html (using html entities for brackets and quotes etc) here
  msgWrapper.innerHTML = successMsg as string;
  successWrapper.appendChild(msgWrapper);

  if (successMsgDesc) {
    const msgDescWrapper = document.createElement('span');
    msgDescWrapper.innerHTML = successMsgDesc as string;
    successWrapper.appendChild(msgDescWrapper);
  }

  successWrapper.focus();
}

function hideAllMsgs(form: HTMLFormElement) {
  const successWrapper = form.querySelector('.success-wrapper');
  const errorsWrapper = form.querySelector('.errors-wrapper');

  if (successWrapper) {
    successWrapper.innerHTML = '';
  }

  if (errorsWrapper) {
    errorsWrapper.innerHTML = '';
  }
}

function resetRecaptcha(form: HTMLFormElement) {
  const recaptcha = window.grecaptcha;

  if (!recaptcha || typeof recaptcha.reset !== 'function') {
    return;
  }

  const widgets = form.querySelectorAll('.g-recaptcha');
  widgets.forEach((_widget, index) => {
    try {
      recaptcha.reset(index);
    } catch (_error) {
      // Ignore invalid widget IDs if they are not available yet.
    }
  });
}

// TODO: This isn't included for some reason...
// https://github.com/sha256/Pristine/issues/37
Pristine.addValidator(
  'equals',
  function(val, otherFieldSelector) {
    let other = document.querySelector(otherFieldSelector);
    return other && ((!val && !other.value) || other.value === val);
  },
  'Fields do not match',
  1,
  true,
);

// Format phone number input value
const isNumericInput = (event) => {
  const key = event.keyCode;
  return ((key >= 48 && key <= 57) || // Allow number line
     (key >= 96 && key <= 105) // Allow number pad
  );
};

const isModifierKey = (event) => {
  const key = event.keyCode;
  return (event.shiftKey === true || key === 35 || key === 36) || // Allow Shift, Home, End
     (key === 8 || key === 9 || key === 13 || key === 46) || // Allow Backspace, Tab, Enter, Delete
     (key > 36 && key < 41) || // Allow left, up, right, down
     (
  // Allow Ctrl/Command + A,C,V,X,Z
       (event.ctrlKey === true || event.metaKey === true) &&
        (key === 65 || key === 67 || key === 86 || key === 88 || key === 90)
     );
};

const enforceFormat = (event) => {
  // Input must be of a valid number format or a modifier key, and not longer than ten digits
  if (!isNumericInput(event) && !isModifierKey(event)) {
    event.preventDefault();
  }
};

const formatToPhone = (event) => {
  if (isModifierKey(event)) {
    return;
  }

  const input = event.target.value.replace(/\D/g, '').substring(0, 10); // First ten digits of input only
  const areaCode = input.substring(0, 3);
  const middle = input.substring(3, 6);
  const last = input.substring(6, 10);

  if (input.length > 6) {
    event.target.value = `(${areaCode}) ${middle}-${last}`;
  } else if (input.length > 3) {
    event.target.value = `(${areaCode}) ${middle}`;
  } else if (input.length > 0) {
    event.target.value = `(${areaCode}`;
  }
};

/* Email confirmation custom validator */
Pristine.addValidator('email-confirmation', function(value, ogEmailId) {
  let emailOg: any = document.querySelector(`#${ogEmailId}`);
  if (value === emailOg.value) {
    return true;
  }
  return false;
}, 'Email does not match', 2, false);

/* Custom required validator based on which step of the form is being submitted */
Pristine.addValidator('step-required', function(value, step) {
  const activeStep: any = document.querySelector('.js-form-step[aria-hidden="false"]')?.getAttribute('data-step');

  if ((activeStep === step) && (value.length === 0)) {
    return false;
  }
  return true;
}, 'This field is required', 1, false);

// /* Custom required validator for google recaptcha */
// Pristine.addValidator("recaptcha", function(value, step) {
// 	const activeStep: any = document.querySelector(`.js-form-step[aria-hidden="false"]`)?.getAttribute('data-step');

// 	console.log(grecaptcha.getResponse(
// 		0
// 	))

// 	// if ((activeStep === step) && (value.length === 0)) {
// 	// 	return false;
// 	// }
// 	return true;

// }, "This field is required", 1, false);

/* Custom word count validator */

function wordsLen(str) {
  const array = str.trim().split(/\s+/);
  return array.length;
}

Pristine.addValidator('word-count', function(value, minWords) {
  const step = this.getAttribute('data-pristine-step-required');
  const activeStep: any = document.querySelector('.js-form-step[aria-hidden="false"]')?.getAttribute('data-step');

  if ((step && (step === activeStep)) || !step) {
    const wordCount = wordsLen(value);
    if (wordCount < minWords) {
      return false;
    }
  }

  return true;
}, 'Minimum ${1} words required', 5, false);

/* Change the form step */
function changeFormSteps(current, newStep) {
  document.querySelector(`.js-form-step[data-step="${current}"]`)?.setAttribute('aria-hidden', 'true');
  document.querySelector(`.js-form-step[data-step="${newStep}"]`)?.setAttribute('aria-hidden', 'false');

  // update the styling of the modal close button based on the form step
  if (newStep === 4) {
    document.querySelector('.contest-modal__close')?.classList.add('--white');
  } else {
    document.querySelector('.contest-modal__close')?.classList.remove('--white');
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  // Hook up receipt file picker
  const receiptEl = document.getElementById('receipt') as HTMLElement;
  let receiptPicker: FilestackPickerUI | null = null;
  if (receiptEl) {
    const maxMedia = parseInt(receiptEl.dataset.maxFiles || '5');
    const maxMediaSize = parseInt(receiptEl.dataset.maxSize || '10');
    const isRequired = receiptEl.dataset.required === 'true';
    receiptPicker = new FilestackPickerUI({
      apiKey: FILESTACK_PUBLIC_API_KEY,
      uiMode: 'THUMBNAILS',
      label: 'Upload receipt',
      required: isRequired,
      // previewElem: document.getElementById('ad-preview-card-img') as HTMLImageElement,
      filestackOptions: {
        // https://www.filestack.com/docs/uploads/pickers/web/#picker-options
        container: receiptEl,
        imageMax: [2000, 2000], // Resizes down on upload
        accept: ['image/jpeg', 'image/png'],
        maxFiles: maxMedia,
        displayMode: PickerDisplayMode.overlay,
        maxSize: maxMediaSize * MB_SIZE,
        fromSources: [
          'local_file_system',
          'googledrive',
          'googlephotos',
          'onedrive',
          'webcam',
        ],
      },
      handlers: {
        onSuccess: async (res: any) => {
          console.debug({ res });
        },
        onDelete: async (file: any) => {
          // console.debug({ file }); // The file object from Filestack or DB

          const result = await deleteMediaFromBackend(file.handle);
          const success = result?.success;
          if (!success) {
            alert('Failed to delete file');
          }
          return !!success;
        },
      },
    });
    await receiptPicker.init();
  }

  const forms = [].slice.call(document.querySelectorAll('.js-form')) as HTMLFormElement[];

  forms.forEach(function(form) {
    //  console.debug('Binding submit event for form', form);
    const pristine = new Pristine(form, {
      classTo: 'form__input-wrapper',
      errorTextParent: 'form__input-wrapper',
    });

    /* Update the phone input to match the format (###) ###-#### */
    const phoneInput : any = form.querySelector('input[name="phone"]');

    if (phoneInput) {
      // phoneInput.addEventListener('keyup', formatePhoneInput);
      phoneInput.addEventListener('keydown', enforceFormat);
      phoneInput.addEventListener('keyup', formatToPhone);
    }

    /* Set up event listeners for next and back buttons */
    const formNextBtns: any = [].slice.call(document.querySelectorAll('.js-next-btn'));
    formNextBtns.forEach(function(nextBtn) {
      nextBtn.addEventListener('click', function() {
        const currentStep: string = this.getAttribute('data-current');
        const nextStep: string = this.getAttribute('data-next');
        if (this.classList.contains('js-validate-btn')) {
          const inputs: any = document.querySelectorAll(`.js-form-step[data-step="${currentStep}"] input`);
          const valid = pristine.validate(inputs, false);
          if (!valid) {
            return;
          }
        }
        changeFormSteps(currentStep, nextStep);
      });
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // console.debug('Form submitted', e);

      // honeypot field
      const honeypot: any = form.querySelector('[name="birthdate"]');
      if (honeypot) {
        const honeypotVal: string = honeypot.value;
        if (honeypotVal.length > 0) {
          return;
        }
      }

      hideAllMsgs(form);

      const valid = pristine.validate();
      const errors = pristine.getErrors();
      console.debug({ errors });

      // console.debug(`valid: ${valid}`);

      if (valid) {
        form.classList.add('loading');
        const route = form.getAttribute('data-route') as string;
        const formData = new FormData(form);
        formData.append('_csrf', csrfToken as string);
        updateInputs(form, true); // disable inputs

        // for (const pair of formData.entries()) {
        // 	console.log(`${pair[0]}, ${pair[1]}`);
        // }

        try {
          const response = await fetch(route, {
            method: 'POST',
            credentials: 'same-origin',
            body: formData,
            headers: {
              accept: 'application/json',
            },
          });

          const json = await response.json();
          console.debug(json);

          // Display any errors returned from the backend
          if (!response.ok) {
            displayErrorsObject(form, json.errors);
            updateInputs(form, false); // enable inputs
            if (form.getAttribute('data-form') === 'contest-entry') {
              resetRecaptcha(form);
            }
            return;
          }

          showSuccess(form);
          form.classList.remove('loading');

          if (form.getAttribute('data-form') === 'contest-entry') {
            const currentStep = document.querySelector('.js-form-step[aria-hidden="false"]')?.getAttribute('data-step');
            changeFormSteps(currentStep, 4);

            // Reset the Filestack input
            await receiptPicker?.reset(false);

            resetRecaptcha(form);

            // Enable inputs
            updateInputs(form, false);
          }
        } catch (error) {
          console.error(error);

          // Generic "error submitting form" message
          displayErrorsObject(form, [ { field: 'error', message: 'Error submitting form' }]);

          // enable inputs
          updateInputs(form, false);

          if (form.getAttribute('data-form') === 'contest-entry') {
            resetRecaptcha(form);
          }
        }
      }
    });
  });
});
